import { useState, useEffect } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import type { SlugStatus } from '../types';
import { generateSlug } from '../utils/formHelpers';

export const useSlugValidation = (initialSlug: string = '', eventName: string = '') => {
  const [slug, setSlug] = useState(initialSlug);
  const [hasManuallyEdited, setHasManuallyEdited] = useState(false);
  const [debouncedSlug] = useDebouncedValue(slug, 500);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');

  // Auto-generate slug from event name if not manually edited
  useEffect(() => {
    if (eventName && !hasManuallyEdited) {
      const generatedSlug = generateSlug(eventName);
      setSlug(generatedSlug);
    }
  }, [eventName, hasManuallyEdited]);

  // Manual slug setter that tracks user edits
  const setSlugManually = (newSlug: string) => {
    setSlug(newSlug);
    setHasManuallyEdited(true);
  };

  // Reset manual edit flag (useful for form resets)
  const resetManualEdit = () => {
    setHasManuallyEdited(false);
  };

  useEffect(() => {
    async function checkSlugAvailability() {
      if (!debouncedSlug || debouncedSlug.length < 3) {
        setSlugStatus('idle');
        return;
      }

      setSlugStatus('checking');

      try {
        const url = `/events/check_slug?slug=${encodeURIComponent(debouncedSlug)}`;
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSlugStatus(data.valid ? 'valid' : 'invalid');
        } else {
          setSlugStatus('idle');
        }
      } catch (error) {
        console.error('Error checking slug availability:', error);
        setSlugStatus('idle');
      }
    }

    checkSlugAvailability();
  }, [debouncedSlug]);

  return {
    slug,
    setSlug: setSlugManually,
    slugStatus,
    hasManuallyEdited,
    resetManualEdit,
  };
};
